import React, { useState, useEffect, useRef } from 'react';
import {
  parseResearchResponse, buildStagingRow, sendApprovedRow,
  STAGING_COLUMNS, validateCandidate,
} from './agentHelpers';
import './ProductDataAgent.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildSearchUrl(store, productName) {
  const q = encodeURIComponent((productName || '').trim());
  if (!q) return null;
  switch (store) {
    case 'Tesco':      return `https://www.tesco.com/groceries/en-GB/search?query=${q}`;
    case 'Sainsburys': return `https://www.sainsburys.co.uk/gol-ui/SearchDisplayView?filters[keyword]=${q}`;
    case 'Aldi':       return `https://www.aldi.co.uk/search?q=${q}`;
    case 'Lidl':       return `https://www.lidl.co.uk/p/all/c1000?q=${q}`;
    default:           return null;
  }
}

// ── Constants ─────────────────────────────────────────────────────────────────

const DISPLAY_COLUMNS = STAGING_COLUMNS.filter(c => c !== 'Date_checked');

const SUGGESTION_CHIPS = [
  'Find all salted butter products across stores',
  'Find semi-skimmed milk across all stores',
  'Find Greek and flavoured yoghurt varieties',
  'Find own-brand baked beans across all stores',
  'Find standard cheddar cheese across stores',
  'Find own-brand olive oils across all 4 stores',
];

// ── Research system prompt ─────────────────────────────────────────────────────
//
// 4-step pipeline: Retrieval → Extraction → Mapping → Output
// Tier is NOT assigned by the AI — the app code maps it deterministically
// from evidence.brandRange via TIER_KEYWORDS. AI must populate brandRange accurately.

const RESEARCH_SYSTEM_PROMPT = `You are a grocery product data agent for ChopMyShop, a UK grocery price comparison app.
You run a 4-step pipeline per candidate. Human reviewers see all evidence before approving.

══════════════════════════════════════════
STEP A — RETRIEVAL
══════════════════════════════════════════
Identify the most likely product for each of the 4 stores: Tesco · Sainsburys · Aldi · Lidl.
Use your training knowledge of UK supermarket product ranges and current listings.

══════════════════════════════════════════
STEP B — EXTRACTION (evidence block)
══════════════════════════════════════════
For each candidate, record what you actually know as raw evidence.
Be strictly honest about source quality:

  priceSource values:
  • "knowledge"  — price is from your training data (may be outdated)
  • "inferred"   — you are estimating/guessing; no reliable data

  If you are uncertain about a price, use "inferred" — DO NOT fabricate a confident number.
  The reviewer will verify "knowledge" prices on the store website.
  "inferred" prices BLOCK approval until manually verified.

══════════════════════════════════════════
STEP C — MAPPING (fields block)
══════════════════════════════════════════
Map evidence to ChopMyShop schema. Apply these rules exactly:

STORE NAMES (exact): Tesco · Sainsburys · Aldi · Lidl

DO NOT assign tier — the app code maps it from evidence.brandRange. Omit tier from fields.

OwnBrand: true for store-label products; false for national brands (Heinz, Kellogg's, Anchor, etc.)

CATEGORIES (snake_case only):
  fruit_veg · fresh_fridge · bakery · tinned_cupboard · snacks · drinks · frozen · meat_poultry · household

VARIANT DICTIONARY (Phase 1 only):
  Butter:      salted | unsalted
  Cheddar:     mild | medium | mature | extra_mature | vintage
  Yogurt fat%: 0_percent | 5_percent | full_fat
  Milk:        NO variant — fat level goes in productId slug
  All others:  OMIT variant field entirely

pricePerUnit calculation (round to 2dp):
  g or ml units → Price / (storeSize / 100)  [per 100g or per 100ml]
  kg units       → Price / (storeSize * 10)   [per 100g]
  L units        → Price / storeSize           [per litre]
  each / pack    → Price / storeSize           [per item]

standardizedSize: family-level constant, same value across all stores for the same productId
productId: snake_case slug including size e.g. salted_butter_250g · baked_beans_400g
Product_name_app: clean display name, no store name or pack size e.g. "Salted Butter"
Link: set to the store search URL for this product, always include it

══════════════════════════════════════════
OUTPUT FORMAT — return ONLY this JSON block
══════════════════════════════════════════
\`\`\`json
{
  "agentLog": ["Step A: Searching for [product]...", "Step B: Recording evidence...", "Step C: Mapping [n] candidates..."],
  "candidates": [
    {
      "status": "needs_review",
      "reasoning": "one sentence explaining confidence and source",
      "missing": ["storeProductId"],
      "flags": [],
      "evidence": {
        "productTitle": "exact product title as you know it",
        "displayedPrice": "£1.45",
        "packSize": "250g",
        "brandRange": "Tesco",
        "url": "https://www.tesco.com/groceries/en-GB/search?query=salted+butter",
        "priceSource": "knowledge",
        "priceNotes": "Price from training data as of early 2024. May have changed."
      },
      "fields": {
        "productId": "salted_butter_250g",
        "Product_name_app": "Salted Butter",
        "category": "fresh_fridge",
        "subcategory": "butter",
        "standardizedSize": 250,
        "unitType": "g",
        "Product_name_store": "Tesco British Salted Butter",
        "variant": "salted",
        "storeSize": 250,
        "OwnBrand": true,
        "Store": "Tesco",
        "Price": 1.45,
        "pricePerUnit": 0.58,
        "confidence": "medium",
        "Link": "https://www.tesco.com/groceries/en-GB/search?query=salted+butter"
      }
    }
  ]
}
\`\`\`

Rules:
• Generate one candidate per store per distinct product variant
• OMIT tier from fields — the app assigns it from evidence.brandRange
• OMIT storeProductId, barcode, imageUrl unless you are certain
• OMIT optional fields (subcategory, variant, promoPrice, promoType, promoEndDate, LastPriceChange) if not applicable
• Always include evidence{} and Link
• Be honest: use priceSource "inferred" if you are not confident in the price`;

// ── Component ─────────────────────────────────────────────────────────────────

export default function ProductDataAgent() {

  // Tab
  const [adminTab, setAdminTab] = useState('agent');

  // Agent tab
  const [prompt,   setPrompt]   = useState('');
  const [running,  setRunning]  = useState(false);
  const [agentLog, setAgentLog] = useState([]);

  // Review queue (persisted)
  const [queue, setQueue] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pda_queue') || '[]'); }
    catch { return []; }
  });

  // Library (persisted)
  const [library, setLibrary] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pda_library') || '[]'); }
    catch { return []; }
  });

  // Card state
  const [expanded,    setExpanded]    = useState(null);
  const [rawView,     setRawView]     = useState({});   // { [id]: true } = show raw evidence
  const [writeStates, setWriteStates] = useState({});

  const logEndRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem('pda_queue',   JSON.stringify(queue));   } catch {}
  }, [queue]);

  useEffect(() => {
    try { localStorage.setItem('pda_library', JSON.stringify(library)); } catch {}
  }, [library]);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [agentLog]);

  // ── API ────────────────────────────────────────────────────────────────────

  const callApi = async (systemPrompt, userMessage, maxTokens = 4000) => {
    const apiKey = (process.env.REACT_APP_ANTHROPIC_API_KEY || '').trim();
    if (!apiKey) throw new Error('REACT_APP_ANTHROPIC_API_KEY is not set in .env');

    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method:  'POST',
      headers: {
        'x-api-key':                                 apiKey,
        'anthropic-version':                         '2023-06-01',
        'content-type':                              'application/json',
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body: JSON.stringify({
        model:      'claude-haiku-4-5-20251001',
        max_tokens: maxTokens,
        system:     systemPrompt,
        messages:   [{ role: 'user', content: userMessage }],
      }),
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      console.error('[ProductDataAgent] API error:', response.status, JSON.stringify(errBody));
      const msg = errBody.error?.message || errBody.message || JSON.stringify(errBody);
      throw new Error(`HTTP ${response.status}: ${msg}`);
    }

    const data = await response.json();
    const text = data.content?.[0]?.text || '';
    if (!text) throw new Error('Empty response from API');
    return text;
  };

  // ── Research ───────────────────────────────────────────────────────────────

  const handleResearch = async () => {
    if (!prompt.trim() || running) return;
    setRunning(true);
    setAgentLog([`→ Researching: "${prompt.trim()}"`]);

    try {
      setAgentLog(prev => [...prev, '→ Calling AI agent…']);
      const text = await callApi(RESEARCH_SYSTEM_PROMPT, prompt.trim());
      console.log('[ProductDataAgent] Research response:', text);

      setAgentLog(prev => [...prev, '→ Parsing results…']);
      const { candidates, agentLog: log } = parseResearchResponse(text);

      if (log.length > 0) setAgentLog(log.map(l => `→ ${l}`));
      setAgentLog(prev => [
        ...prev,
        `→ ${candidates.length} candidate${candidates.length !== 1 ? 's' : ''} added to review queue`,
      ]);

      setQueue(prev => [...candidates, ...prev]);
      if (candidates.length > 0) setTimeout(() => setAdminTab('review'), 700);

    } catch (err) {
      console.error('[ProductDataAgent] Research error:', err);
      setAgentLog(prev => [...prev, `✕ ${err.message}`]);
    } finally {
      setRunning(false);
    }
  };

  // ── Approve / Reject ───────────────────────────────────────────────────────

  const handleApprove = async (item) => {
    setWriteStates(prev => ({ ...prev, [item.id]: 'writing' }));
    try {
      await sendApprovedRow(buildStagingRow(item.fields));
      setQueue(prev    => prev.filter(i => i.id !== item.id));
      setLibrary(prev  => [{ ...item, approvedAt: new Date().toISOString() }, ...prev]);
      if (expanded === item.id) setExpanded(null);
    } catch (err) {
      console.error('[ProductDataAgent] Approve error:', err);
      setWriteStates(prev => ({ ...prev, [item.id]: 'error' }));
    }
  };

  const handleReject    = (id) => { setQueue(prev => prev.filter(i => i.id !== id)); if (expanded === id) setExpanded(null); };
  const handleRejectAll = ()   => { setQueue([]); setExpanded(null); };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="pda-root">

      {/* ── Admin tabs ─────────────────────────────────────────────────────── */}
      <div className="pda-admin-tabs">
        {[
          { key: 'agent',   label: 'AI Agent',  badge: null },
          { key: 'review',  label: 'Review',    badge: queue.length   || null },
          { key: 'library', label: 'Library',   badge: library.length || null, green: true },
        ].map(({ key, label, badge, green }) => (
          <button
            key={key}
            className={`pda-admin-tab${adminTab === key ? ' pda-admin-tab--active' : ''}`}
            onClick={() => setAdminTab(key)}
          >
            {label}
            {badge > 0 && (
              <span className={`pda-tab-badge${green ? ' pda-tab-badge--green' : ''}`}>{badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════
          AI AGENT TAB
      ══════════════════════════════════════════ */}
      {adminTab === 'agent' && (
        <div className="pda-agent-section">
          <div className="pda-agent-card">
            <h2 className="pda-agent-title">Research products</h2>
            <p className="pda-agent-subtitle">
              Describe what to find. The agent generates candidates across all 4 stores
              — you review and approve before anything enters the library.
            </p>

            <label className="pda-label">RESEARCH PROMPT</label>
            <textarea
              className="pda-prompt-textarea"
              rows={3}
              placeholder="e.g. Find all salted butter products across Tesco, Sainsburys, Aldi and Lidl"
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleResearch(); }}
              disabled={running}
            />

            <div className="pda-chips">
              {SUGGESTION_CHIPS.map(chip => (
                <button
                  key={chip}
                  className="pda-chip"
                  onClick={() => setPrompt(chip)}
                  disabled={running}
                >
                  {chip}
                </button>
              ))}
            </div>

            <button
              className="pda-research-btn"
              onClick={handleResearch}
              disabled={running || !prompt.trim()}
            >
              {running
                ? <><span className="pda-spinner" aria-hidden="true" /> Researching…</>
                : 'Research with Penny'}
            </button>
          </div>

          {agentLog.length > 0 && (
            <div className="pda-agent-log">
              {agentLog.map((line, i) => (
                <div key={i} className={`pda-log-line${line.startsWith('✕') ? ' pda-log-line--error' : ''}`}>
                  {line}
                </div>
              ))}
              <div ref={logEndRef} />
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════
          REVIEW TAB
      ══════════════════════════════════════════ */}
      {adminTab === 'review' && (
        <div className="pda-review-section">
          {queue.length === 0 ? (
            <div className="pda-empty-state">
              <p>No products in the review queue.</p>
              <button className="pda-ghost-btn" onClick={() => setAdminTab('agent')}>
                Run a research prompt →
              </button>
            </div>
          ) : (
            <>
              <div className="pda-review-toolbar">
                <span className="pda-review-count">
                  {queue.length} product{queue.length !== 1 ? 's' : ''} awaiting review
                </span>
                <button className="pda-ghost-btn pda-ghost-btn--danger" onClick={handleRejectAll}>
                  Reject all
                </button>
              </div>

              {queue.map(item => {
                const isExpanded   = expanded === item.id;
                const wState       = writeStates[item.id] || 'idle';
                const name         = item.fields.Product_name_app || item.fields.Product_name_store || 'Unknown product';
                const store        = item.fields.Store        || '—';
                const tier         = item.fields.tier;
                const confidence   = item.fields.confidence;
                const price        = item.fields.Price != null ? `£${Number(item.fields.Price).toFixed(2)}` : null;
                const missingCount = item.missing?.length || 0;

                // Validation
                const { errors: valErrors, warnings: valWarnings } = validateCandidate(item.fields);
                const canApprove   = valErrors.length === 0;
                const searchUrl    = item.fields.Link || buildSearchUrl(item.fields.Store, item.fields.Product_name_app || item.fields.Product_name_store);
                const storeName    = item.fields.Store || 'Store';

                return (
                  <div key={item.id} className={`pda-review-card${isExpanded ? ' pda-review-card--expanded' : ''}`}>

                    {/* Collapsed header */}
                    <button
                      className="pda-card-header"
                      onClick={() => setExpanded(isExpanded ? null : item.id)}
                      aria-expanded={isExpanded}
                    >
                      <div className="pda-card-header-left">
                        <span className="pda-card-store">{store}</span>
                        <span className="pda-card-name">{name}</span>
                        {price && <span className="pda-card-price">{price}</span>}
                      </div>
                      <div className="pda-card-header-right">
                        {tier       && <span className={`pda-meta-chip pda-meta-chip--${tier}`}>{tier}</span>}
                        {confidence && <span className="pda-meta-chip pda-meta-chip--conf">{confidence}</span>}
                        {valErrors.length > 0 && (
                          <span className="pda-meta-chip pda-meta-chip--blocked">{valErrors.length} error{valErrors.length > 1 ? 's' : ''}</span>
                        )}
                        {valErrors.length === 0 && valWarnings.length > 0 && (
                          <span className="pda-meta-chip pda-meta-chip--warn">{valWarnings.length} warning{valWarnings.length > 1 ? 's' : ''}</span>
                        )}
                        {missingCount > 0 && (
                          <span className="pda-meta-chip pda-meta-chip--missing">{missingCount} to review</span>
                        )}
                        <span className="pda-card-chevron" aria-hidden="true">{isExpanded ? '▲' : '▼'}</span>
                      </div>
                    </button>

                    {/* Expanded body */}
                    {isExpanded && (
                      <div className="pda-card-body">

                        {/* ── Validation errors (block approval) */}
                        {valErrors.length > 0 && (
                          <div className="pda-validation pda-validation--error">
                            <span className="pda-validation-label">✕ Schema errors — approval blocked</span>
                            <ul className="pda-validation-list">
                              {valErrors.map((e, i) => <li key={i}>{e}</li>)}
                            </ul>
                          </div>
                        )}

                        {/* ── Validation warnings */}
                        {valWarnings.length > 0 && (
                          <div className="pda-validation pda-validation--warning">
                            <span className="pda-validation-label">⚠ Warnings — verify before approving</span>
                            <ul className="pda-validation-list">
                              {valWarnings.map((w, i) => <li key={i}>{w}</li>)}
                            </ul>
                          </div>
                        )}

                        {/* ── Evidence snapshot + Raw / Mapped toggle */}
                        {item.evidence && (
                          <div className="pda-evidence-section">
                            <div className="pda-evidence-header">
                              <span className="pda-evidence-title">EVIDENCE</span>
                              <span className={`pda-price-source-badge pda-price-source--${item.evidence.priceSource || 'inferred'}`}>
                                {item.evidence.priceSource === 'knowledge'
                                  ? 'Training data'
                                  : item.evidence.priceSource === 'scraped'
                                  ? '✓ Scraped'
                                  : 'Inferred — verify'}
                              </span>
                              <div className="pda-view-toggle">
                                <button
                                  className={`pda-toggle-btn${!rawView[item.id] ? ' pda-toggle-btn--active' : ''}`}
                                  onClick={() => setRawView(prev => ({ ...prev, [item.id]: false }))}
                                >Mapped</button>
                                <button
                                  className={`pda-toggle-btn${rawView[item.id] ? ' pda-toggle-btn--active' : ''}`}
                                  onClick={() => setRawView(prev => ({ ...prev, [item.id]: true }))}
                                >Raw</button>
                              </div>
                            </div>

                            {/* Raw evidence view */}
                            {rawView[item.id] && (
                              <div className="pda-evidence-table">
                                {[
                                  ['Product title',   item.evidence.productTitle],
                                  ['Displayed price', item.evidence.displayedPrice],
                                  ['Pack size',       item.evidence.packSize],
                                  ['Brand / range',   item.evidence.brandRange],
                                  ['Price notes',     item.evidence.priceNotes],
                                ].map(([label, val]) => (
                                  <div key={label} className="pda-evidence-row">
                                    <span className="pda-evidence-key">{label}</span>
                                    <span className={`pda-evidence-val${!val ? ' pda-field-val--empty' : ''}`}>
                                      {val || '—'}
                                    </span>
                                  </div>
                                ))}
                                {searchUrl && (
                                  <div className="pda-evidence-row">
                                    <span className="pda-evidence-key">Verify URL</span>
                                    <a className="pda-source-link" href={searchUrl} target="_blank" rel="noopener noreferrer">
                                      {item.fields.Link ? 'View product page ↗' : `Search on ${storeName} ↗`}
                                    </a>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* ── Source row (when no evidence block) */}
                        {!item.evidence && (
                          <div className="pda-source-row">
                            <span className="pda-source-label">SOURCE</span>
                            {searchUrl ? (
                              <a className="pda-source-link" href={searchUrl} target="_blank" rel="noopener noreferrer">
                                {item.fields.Link ? 'View source ↗' : `Search on ${storeName} ↗`}
                              </a>
                            ) : (
                              <span className="pda-source-none">No source URL</span>
                            )}
                            <span className="pda-source-ai-badge">AI estimate</span>
                          </div>
                        )}

                        <p className="pda-card-reasoning">{item.reasoning}</p>

                        {item.missing?.length > 0 && (
                          <div className="pda-missing">
                            <span className="pda-missing-label">Needs review:</span>
                            {item.missing.map(f => <span key={f} className="pda-missing-chip">{f}</span>)}
                          </div>
                        )}

                        {item.flags?.length > 0 && (
                          <div className="pda-flags">
                            {item.flags.map(f => <span key={f} className="pda-flag-chip">{f.replace(/_/g, ' ')}</span>)}
                          </div>
                        )}

                        {/* ── Mapped fields table (hidden in raw view) */}
                        {!rawView[item.id] && (
                          <div className="pda-fields-table">
                            {DISPLAY_COLUMNS.map(col => {
                              const val      = item.fields[col];
                              const isEmpty  = val === '' || val === null || val === undefined;
                              const isReview = item.missing?.includes(col);
                              const isError  = valErrors.some(e => e.includes(col));
                              return (
                                <div key={col} className={`pda-field-row${isReview ? ' pda-field-row--review' : ''}${isError ? ' pda-field-row--error' : ''}`}>
                                  <span className="pda-field-key">{col}</span>
                                  <span className={`pda-field-val${isEmpty ? ' pda-field-val--empty' : ''}`}>
                                    {isEmpty ? (isReview ? 'needs review' : '—') : String(val)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        <div className="pda-card-actions">
                          {wState === 'idle' && (
                            <>
                              <button
                                className={`pda-approve-btn${!canApprove ? ' pda-approve-btn--blocked' : valWarnings.length > 0 ? ' pda-approve-btn--warn' : ''}`}
                                onClick={() => canApprove && handleApprove(item)}
                                disabled={!canApprove}
                                title={!canApprove ? valErrors[0] : undefined}
                              >
                                {canApprove ? '✓ Approve — send to agent_staging' : '✕ Fix errors to approve'}
                              </button>
                              <button className="pda-reject-btn" onClick={() => handleReject(item.id)}>
                                ✕ Reject
                              </button>
                            </>
                          )}
                          {wState === 'writing' && (
                            <div className="pda-write-status pda-write--writing">
                              <span className="pda-spinner pda-spinner--sm" aria-hidden="true" />
                              Writing to agent_staging…
                            </div>
                          )}
                          {wState === 'error' && (
                            <div className="pda-write-status pda-write--error">
                              ✕ Write failed
                              <button className="pda-retry-btn" onClick={() => handleApprove(item)}>Retry</button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════
          LIBRARY TAB
      ══════════════════════════════════════════ */}
      {adminTab === 'library' && (
        <div className="pda-library-section">
          {library.length === 0 ? (
            <div className="pda-empty-state">
              <p>No approved products yet.</p>
              <button className="pda-ghost-btn" onClick={() => setAdminTab('agent')}>
                Start researching →
              </button>
            </div>
          ) : (
            <>
              <div className="pda-review-toolbar">
                <span className="pda-review-count">
                  {library.length} approved product{library.length !== 1 ? 's' : ''}
                </span>
                <button
                  className="pda-ghost-btn pda-ghost-btn--danger"
                  onClick={() => { if (window.confirm('Clear library history?')) setLibrary([]); }}
                >
                  Clear
                </button>
              </div>

              {library.map(item => (
                <div key={item.id} className="pda-library-row">
                  <div className="pda-library-row-left">
                    <span className="pda-card-store">{item.fields.Store || '—'}</span>
                    <span className="pda-card-name">
                      {item.fields.Product_name_app || item.fields.Product_name_store || 'Unknown'}
                    </span>
                  </div>
                  <div className="pda-library-row-right">
                    {item.fields.Price != null && (
                      <span className="pda-card-price">£{Number(item.fields.Price).toFixed(2)}</span>
                    )}
                    <span className="pda-meta-chip pda-meta-chip--approved">✓ approved</span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

    </div>
  );
}
